import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Popover } from 'components/atoms/popover';
import { BodyShort01, Label01, SingleLineEllipsis } from 'components/styles';
import { ProductiveHeading01 } from 'ozone/atoms';
import { CategorySummary } from '../reports';
import { CategoryName } from './category-name';
import { FormatTime } from './format-time';

interface Props {
  categories: CategorySummary[];
  className?: string;
}
const SmallTrashIcon = styled(TrashCan16)`
  width: 12px;
  height: 12px;
`;
const StyledTable = styled.table`
  table-layout: fixed;
`;
const Wrapper = styled.div`
  padding: var(--spacing-05);
`;

const Label = styled.td`
  ${BodyShort01};
  ${SingleLineEllipsis};
  color: var(--gray-60);
  margin-right: var(--spacing-05);
`;

const SmallLabel = styled(Label)`
  ${Label01};
`;
const Values = styled.td`
  ${BodyShort01};
`;
const SecondaryValue = styled.span`
  color: var(--gray-60);
`;

const Row = styled.tr`
  ${Label},
  ${SmallLabel} {
    padding: var(--spacing-03) var(--spacing-05) var(--spacing-03) 0;
  }
  ${Values} {
    text-align: right;
  }
  ${SecondaryValue} {
    margin-left: var(--spacing-02);
  }
`;

export const CategoriesSummaryList: FC<
  Props & {
    labelSize?: string;
  }
> = ({ categories, className, labelSize = 'large' }) => {
  return (
    <StyledTable className={className}>
      {categories.map(({ label, minutes, percentage, deleted }) => (
        <Row key={label}>
          {labelSize === 'small' ? (
            <SmallLabel>
              <CategoryName
                deleted={deleted}
                name={label}
                trashIcon={<SmallTrashIcon />}
              />
            </SmallLabel>
          ) : (
            <Label>
              <CategoryName deleted={deleted} name={label} />
            </Label>
          )}
          <Values>
            <span>{percentage.toFixed(1)}%</span>
            <SecondaryValue>
              (<FormatTime value={minutes} />)
            </SecondaryValue>
          </Values>
        </Row>
      ))}
    </StyledTable>
  );
};

const Content: FC<Props> = ({ categories, className }) => (
  <Wrapper className={className}>
    <Space direction="vertical" size={16}>
      <ProductiveHeading01>
        <FormattedMessage id="timesheets.summary.categories" />
      </ProductiveHeading01>
      <CategoriesSummaryList categories={categories} labelSize="small" />
    </Space>
  </Wrapper>
);
const Bar = styled.div<{ percent }>`
  height: 100%;
  background-color: var(--blue-10);
  width: 16px;
  display: inline-block;
  position: relative;
  border-bottom: 1px solid var(--blue-40);

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    background-color: var(--blue-40);
    bottom: 0;
    left: 0;
    max-height: ${({ percent }) => `${percent}%`};
    animation: grow 0.5s ease-in forwards;
  }

  @keyframes grow {
    0% {
      height: 0;
    }
    100% {
      height: 100%;
    }
  }
`;

const BarContainer = styled.div`
  height: 100%;

  ${Bar} {
    margin-right: var(--spacing-02);

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const CategoriesChart: FC<Props> = ({ categories, className }) => {
  return categories.length !== 0 ? (
    <Popover content={<Content categories={categories} />} placement="leftTop">
      <BarContainer className={className}>
        {categories.map(({ label, percentage }) => (
          <Bar key={label} percent={percentage.toFixed(1)} />
        ))}
      </BarContainer>
    </Popover>
  ) : (
    <>—</>
  );
};
